// bluaIQ — Emails Module
// AI-generated emails: material orders, quote requests, schedule reminders

import { apiFetch } from '../utils/api'
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Mail, Send, Loader2, ShoppingCart, MessageSquare, Clock, Check, AlertCircle, User, Truck, ArrowRight, RefreshCw } from 'lucide-react'
import { isDemoProject } from '../utils/demoData'
import SavedProjectOnlyNotice from './SavedProjectOnlyNotice'

// ── TYPES ─────────────────────────────────────────

interface EmailDraft {
  id: string
  type: string
  to?: string
  subject: string
  body: string
  status: string
  sentAt?: string
  createdAt: string
  // PR-NEW: surfaced for the list-level "Draft updated …" date
  // line. Optional because legacy DB rows may not carry it; the
  // FE falls back to createdAt when missing.
  updatedAt?: string
  threadId?: string | null
}

// PR-NEW: Level 3 thread + message shapes returned by the
// new GET /api/projects/:id/email-threads and
// GET /api/email-threads/:id routes.
interface EmailThread {
  id: string
  projectId: string
  userId: string
  sourceType: string
  sourceId?: string | null
  subject: string
  status: string
  recipientEmail?: string | null
  recipientName?: string | null
  replyAddress?: string | null
  lastInboundAt?: string | null
  closedAt?: string | null
  createdAt: string
  updatedAt: string
  // PR-NEW: list endpoint now returns nested rows so the FE can
  // render a Replies section + an Archived section without N+1
  // requests. Optional because legacy callers (and the
  // /email-threads/:id detail endpoint) shape these differently.
  messages?: EmailMessage[]
  drafts?: EmailDraft[]
}
interface EmailMessage {
  id: string
  threadId: string
  direction: 'OUTBOUND' | 'INBOUND'
  fromEmail: string
  fromName?: string | null
  toEmail: string
  subject: string
  textBody: string
  htmlBody?: string | null
  status: string
  providerMessageId?: string | null
  sentAt?: string | null
  receivedAt?: string | null
  createdAt: string
}
interface EmailThreadDetail extends EmailThread {
  messages: EmailMessage[]
  drafts: EmailDraft[]
}

interface Contact {
  id: string
  name: string
  email?: string
  role?: string
  company?: string
}

// PR-NEW: full display map — covers every type that may exist
// on an existing DB row (including the legacy `material_order`
// drafts/sent emails) so the EmailCard renders a localized
// label for them. The legacy `material_order` row still
// displays, but new material-order drafts are no longer
// generated from this UI — they live in the Material Orders
// module (see MATERIAL_ORDER_REDIRECT_TARGET below).
const EMAIL_TYPE_LABELS: Record<string, { en: string; es: string; icon: typeof Mail }> = {
  material_order:    { en: 'Material Order',    es: 'Pedido de Materiales',    icon: ShoppingCart },
  quote_request:     { en: 'Quote Request',     es: 'Solicitud de Cotización', icon: MessageSquare },
  agenda_reminder:   { en: 'Agenda Reminder',   es: 'Recordatorio de Agenda',  icon: Clock },
  schedule_reminder: { en: 'Agenda Reminder',   es: 'Recordatorio de Agenda',  icon: Clock },
  general_message:   { en: 'General Message',   es: 'Mensaje general',         icon: Mail },
}

// PR-NEW: selector-visible types only. Material orders live in
// the Material Orders module (link below). Schedule reminders
// were renamed "Agenda Reminder" per product copy.
const SELECTABLE_EMAIL_TYPES: Record<string, { en: string; es: string; icon: typeof Mail }> = {
  quote_request:   EMAIL_TYPE_LABELS.quote_request,
  agenda_reminder: EMAIL_TYPE_LABELS.agenda_reminder,
  general_message: EMAIL_TYPE_LABELS.general_message,
}

// Back-compat alias — older code references EMAIL_TYPES.
const EMAIL_TYPES = EMAIL_TYPE_LABELS

// ── DEMO DATA ───────────────────────────────────

const DEMO_EMAILS: EmailDraft[] = [
  {
    id: 'em-1', type: 'material_order', to: 'quotes@84lumber.com', status: 'SENT',
    subject: 'Material Order — 4521 Legacy Dr, Frisco TX (Framing Package)',
    body: 'Hi Mike,\n\nPlease prepare the following framing package for delivery to 4521 Legacy Dr, Frisco TX 75034:\n\n- 380 pcs 2x4x8 KD SPF Stud\n- 92 pcs 2x6x16 SPF\n- 68 sheets OSB 7/16 4x8\n- 4 pcs LVL 1.75x11.25x20\n- 48 pcs Simpson LTP4 Tie Plates\n\nDelivery requested by Feb 25. Please confirm availability and delivery time.\n\nThank you,\nNATAMI User',
    sentAt: '2026-02-18T00:00:00Z', createdAt: '2026-02-18T00:00:00Z',
  },
  {
    id: 'em-2', type: 'quote_request', to: 'orders@becn.com', status: 'SENT',
    subject: 'RFQ — Roofing Materials for 1,800 sqft Spec Home (Frisco)',
    body: 'Hello,\n\nI need a quote for the following roofing materials for a 1,800 sqft spec home in Frisco TX:\n\n- 22 squares OC Duration Shingles (Onyx Black)\n- 3 rolls Synthetic Underlayment (10 sq/roll)\n- 24 pcs Drip Edge 10ft Galv\n- 2 rolls Ice & Water Shield (2 sq/roll)\n- 12 pcs Ridge Vent 4ft\n\nPlease include pricing, delivery timeline, and payment terms.\n\nBest regards,\nNATAMI User',
    sentAt: '2026-03-01T00:00:00Z', createdAt: '2026-03-01T00:00:00Z',
  },
  {
    id: 'em-3', type: 'schedule_reminder', to: 'jtorres@torresplumbing.com', status: 'DRAFT',
    subject: 'Reminder: Plumbing Rough-In Inspection — Mar 28',
    body: 'Hi Juan,\n\nJust a reminder that the plumbing rough-in inspection is scheduled for Friday, March 28.\n\nPlease make sure all rough-in work is complete and accessible for the inspector by 8:00 AM.\n\nLet me know if there are any issues or if we need to reschedule.\n\nThanks,\nNATAMI User',
    createdAt: '2026-03-22T00:00:00Z',
  },
  {
    id: 'em-4', type: 'material_order', status: 'DRAFT',
    subject: 'Insulation Order — 4521 Legacy Dr',
    body: 'Hi,\n\nPlease prepare the following insulation materials:\n\n- 12x R-38 Fiberglass Batt 24" (8-pack)\n- 8x R-13 Wall Insulation 15" (12-pack)\n- 6x Spray Foam Sealant 12oz\n- 2x Vapor Barrier 10x100 6mil\n\nDelivery to 4521 Legacy Dr, Frisco TX 75034.\nNeeded by April 5.\n\nPlease confirm.\nThank you',
    createdAt: '2026-03-20T00:00:00Z',
  },
]

// ── COMPONENT ─────────────────────────────────────

export default function EmailsModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const isEs = i18n.language === 'es'
  const [showGenerator, setShowGenerator] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)

  const { data: apiEmails, isLoading } = useQuery<EmailDraft[]>({
    queryKey: ['emails', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/emails`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })
  const isDemo = isDemoProject(projectId)
  const emails = Array.isArray(apiEmails) ? apiEmails : (isDemo ? DEMO_EMAILS : [])

  // PR-NEW: bilingual map from backend error codes to
  // user-facing messages. Used by both Send and Save Draft so
  // the user never sees raw `invalid_input` / `invalid_recipient`.
  const friendlyEmailError = (rawError: string | undefined | null): string => {
    const code = (rawError || '').toString().toLowerCase()
    if (code === 'invalid_recipient' || code === 'invalid_input') {
      return isEs
        ? 'Ingresa un correo válido para el destinatario.'
        : 'Please enter a valid recipient email.'
    }
    // Generic send/save failure copy per spec.
    return isEs
      ? 'No se pudo enviar el correo. Tu borrador no fue enviado.'
      : 'Could not send email. Your draft was not sent.'
  }

  const sendMutation = useMutation({
    mutationFn: async (emailId: string) => {
      // PR #138B: defense-in-depth. The Generate-Email entry button
      // and the Reply/Archive buttons are already gated by !isDemo;
      // this short-circuits the actual SendGrid POST in case a demo
      // email card (rendered from DEMO_EMAILS) ever reaches Send.
      if (isDemo) return { success: false, demo: true }
      const token = await getToken()
      const r = await apiFetch(`/api/emails/${emailId}/send`, { method: 'POST', headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } })
      const data = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(friendlyEmailError(data?.error))
      return data
    },
    // PR-NEW: after Send succeeds we must update EVERY surface
    // that shows DRAFT/SENT status, not just the legacy list.
    // Previously only `['emails', …]` was invalidated, so the
    // open thread panel kept rendering the DRAFT version of the
    // card (with an active Send button) until the user refreshed.
    // Now we invalidate:
    //   - legacy drafts/sent list (?)
    //   - the open thread detail (flips DRAFT card → SENT row)
    //   - the conversation list (header badge → "Sent")
    //   - Material Orders threads-summary (per-row badge in the
    //     schedule changes from DRAFT → "Email sent / Correo enviado")
    onSuccess: () => {
      setSendError(null)
      queryClient.invalidateQueries({ queryKey: ['emails', projectId] })
      queryClient.invalidateQueries({ queryKey: ['email-threads', projectId] })
      if (selectedThreadId) {
        queryClient.invalidateQueries({ queryKey: ['email-thread', selectedThreadId] })
      }
      queryClient.invalidateQueries({ queryKey: ['material-orders-threads-summary', projectId] })
    },
    onError: (err: Error) => { setSendError(err.message) }
  })

  // PR-NEW: flexible draft-update mutation. Replaces the older
  // to-only mutation so the composer can PATCH any of {to, subject,
  // body}. The existing /api/emails/:id PATCH route already accepts
  // all three fields, is requirePro, owner-scoped, and gated on
  // status === 'DRAFT', so no new backend route is required.
  // Invalidations cascade to every surface that shows the draft:
  // legacy list, open thread detail, conversation list, and the
  // Material Orders threads-summary (in case a downstream consumer
  // shows the subject).
  const updateMutation = useMutation({
    mutationFn: async ({ emailId, to, subject, body }: {
      emailId: string
      to?: string | null
      subject?: string
      body?: string
    }) => {
      // PR #138B: defense-in-depth — block draft PATCH on demo.
      if (isDemo) return { success: false, demo: true }
      const token = await getToken()
      const payload: Record<string, unknown> = {}
      if (to !== undefined) payload.to = to
      if (subject !== undefined) payload.subject = subject
      if (body !== undefined) payload.body = body
      const r = await apiFetch(`/api/emails/${emailId}`, {
        method: 'PATCH',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!r.ok) {
        const err = await r.json().catch(() => ({}))
        // PR-NEW: surface a bilingual user-facing message
        // (Please enter a valid recipient email / Ingresa un
        // correo válido para el destinatario) instead of the
        // raw `invalid_recipient` / `invalid_input` code from
        // the PATCH route.
        throw new Error(friendlyEmailError(err?.error))
      }
      return r.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails', projectId] })
      queryClient.invalidateQueries({ queryKey: ['email-threads', projectId] })
      if (selectedThreadId) {
        queryClient.invalidateQueries({ queryKey: ['email-thread', selectedThreadId] })
      }
    },
  })

  // PR-NEW: delete-draft mutation. Calls the new owner-scoped
  // DELETE /api/emails/:id route. Cascade invalidates every
  // surface that may have shown the draft so the Drafts count,
  // open thread panel, conversation list, and Material Orders
  // schedule all update immediately. If the server reports
  // `threadDeleted: true` (thread had no other history), the
  // selected ?thread=<id> URL param is cleared so the user
  // doesn't sit on a 404'd thread detail.
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleteToast, setDeleteToast] = useState<string | null>(null)
  const deleteMutation = useMutation({
    mutationFn: async (emailId: string): Promise<{ success: boolean; threadDeleted?: boolean }> => {
      // PR #138B: defense-in-depth — block draft delete on demo.
      if (isDemo) return { success: false }
      const token = await getToken()
      const r = await apiFetch(`/api/emails/${emailId}`, {
        method: 'DELETE',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      })
      if (!r.ok) {
        const err = await r.json().catch(() => ({}))
        throw new Error(err?.error || `Delete failed (${r.status})`)
      }
      return r.json()
    },
    onSuccess: (data) => {
      setDeleteError(null)
      setDeleteToast(isEs ? 'Borrador eliminado' : 'Draft deleted')
      setTimeout(() => setDeleteToast(null), 3500)
      queryClient.invalidateQueries({ queryKey: ['emails', projectId] })
      queryClient.invalidateQueries({ queryKey: ['email-threads', projectId] })
      if (selectedThreadId) {
        queryClient.invalidateQueries({ queryKey: ['email-thread', selectedThreadId] })
      }
      queryClient.invalidateQueries({ queryKey: ['material-orders-threads-summary', projectId] })
      // If the server deleted the underlying thread (no other
      // history), navigate back to the email list so we don't
      // sit on a stale ?thread=<deleted-id> URL.
      if (data?.threadDeleted && selectedThreadId) {
        const next = new URLSearchParams(searchParams)
        next.delete('thread')
        setSearchParams(next, { replace: true })
      }
    },
    onError: (err: Error) => {
      setDeleteError(err?.message || (isEs ? 'No se pudo eliminar el borrador' : 'Could not delete draft'))
    },
  })

  const drafts = emails?.filter(e => e.status === 'DRAFT') || []
  const sent = emails?.filter(e => e.status === 'SENT') || []

  // PR-NEW: Level 3 threads. The "By thread" view groups
  // outbound + inbound messages by EmailThread so a supplier
  // reply lands in the same conversation. Threads come from
  // the new /api/projects/:id/email-threads endpoint added in
  // PR #114. The selected thread id can be passed via the
  // ?thread= URL param so Material Orders can hand off to
  // Emails after creating a thread (deep-link).
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedThreadId = searchParams.get('thread')
  const {
    data: threadsRaw,
    refetch: refetchThreads,
    isFetching: isRefreshingThreads,
  } = useQuery<EmailThread[]>({
    queryKey: ['email-threads', projectId],
    queryFn: async () => {
      const token = await getToken()
      const r = await apiFetch(`/api/projects/${projectId}/email-threads`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    },
    retry: false,
    enabled: !!projectId,
    // PR-NEW: refetch on window focus + poll lightly while at
    // least one thread is SENT and awaiting a reply. Without
    // this, the list only refreshes after a mutation — and a
    // supplier reply landing while the user has the list open
    // would not surface until they navigate away and back.
    refetchOnWindowFocus: true,
    refetchInterval: (q) => {
      const list = q.state.data as EmailThread[] | undefined
      if (!Array.isArray(list)) return false
      const hasWaiting = list.some((t) => t.status === 'SENT' && !t.lastInboundAt)
      return hasWaiting ? 60_000 : false
    },
  })
  const threads = Array.isArray(threadsRaw) ? threadsRaw : []

  // PR-NEW: mailbox tabs. Production report: when there were many
  // SENT emails the Replies / Archived sections were scrolled off
  // the bottom of the page and the user thought they didn't exist.
  // Tabs at the top of the list give every mailbox a permanent
  // visible entry point with a live count.
  type Mailbox = 'ALL' | 'DRAFTS' | 'SENT' | 'REPLIES' | 'ARCHIVED'
  const [activeMailbox, setActiveMailbox] = useState<Mailbox>('ALL')

  // Counts computed once per render. INBOUND messages are counted
  // off the non-archived threads (CLOSED threads' replies are
  // surfaced inside the Archived tab instead).
  const inboundReplyCount = threads
    .filter((th) => th.status !== 'CLOSED')
    .reduce(
      (n, th) =>
        n + ((th.messages || []).filter((m) => m.direction === 'INBOUND').length),
      0,
    )
  const archivedCount = threads.filter((th) => th.status === 'CLOSED').length
  const draftsCount = (emails?.filter((e) => e.status === 'DRAFT') || []).length
  const sentCount = (emails?.filter((e) => e.status === 'SENT') || []).length
  const allCount = draftsCount + sentCount + inboundReplyCount + archivedCount

  // PR-NEW: manual refresh of every email surface — used by the
  // list-level "Refresh / Actualizar" button. The selected thread
  // detail already has its own refresh affordance; this one is
  // for the list page where there was previously no way to ask
  // the UI to re-check for new inbound replies.
  const refreshAllEmails = () => {
    queryClient.invalidateQueries({ queryKey: ['emails', projectId] })
    refetchThreads()
  }

  const {
    data: selectedThread,
    refetch: refetchSelectedThread,
    isFetching: isRefreshingThread,
  } = useQuery<EmailThreadDetail>({
    queryKey: ['email-thread', selectedThreadId],
    queryFn: async () => {
      const token = await getToken()
      const r = await apiFetch(`/api/email-threads/${selectedThreadId}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    },
    retry: false,
    enabled: !!selectedThreadId,
    // Refresh inbound replies when the user returns to the tab,
    // and poll lightly while a SENT conversation is awaiting a
    // reply. This is intentionally gentle (60s) so it doesn't
    // hammer the API while still feeling "live".
    refetchOnWindowFocus: true,
    refetchInterval: (q) => {
      const t = q.state.data as EmailThreadDetail | undefined
      if (!t) return false
      const isWaiting = t.status === 'SENT' && !(t.messages || []).some((m) => m.direction === 'INBOUND')
      return isWaiting ? 60_000 : false
    },
  })

  const closeThreadMutation = useMutation({
    mutationFn: async (threadId: string) => {
      // PR #138B: defense-in-depth — block thread close on demo.
      if (isDemo) return { success: false }
      const token = await getToken()
      const r = await apiFetch(`/api/email-threads/${threadId}`, {
        method: 'PATCH',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: 'CLOSED' }),
      })
      if (!r.ok) throw new Error('Close failed')
      return r.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-threads', projectId] })
      queryClient.invalidateQueries({ queryKey: ['email-thread', selectedThreadId] })
    },
  })

  // PR-NEW: reply-to-supplier mutation. POSTs to the new
  // /api/email-threads/:threadId/reply-draft route which creates
  // (or reuses) a DRAFT EmailDraft linked to the thread, with
  // To = latest INBOUND fromEmail and Subject = "Re: …". The
  // server may return `reused: true` if an open DRAFT already
  // exists on the thread — we treat both the same.
  const [replyError, setReplyError] = useState<string | null>(null)
  const replyDraftMutation = useMutation({
    mutationFn: async (threadId: string) => {
      // PR #138B: defense-in-depth — block reply-draft creation on demo.
      if (isDemo) return { success: false }
      const token = await getToken()
      const r = await apiFetch(`/api/email-threads/${threadId}/reply-draft`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      const data = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(data?.error || `Reply failed (${r.status})`)
      return data as { draft: EmailDraft; reused: boolean }
    },
    onSuccess: () => {
      setReplyError(null)
      // Refresh the thread detail so the new DRAFT EmailMessage
      // panel renders. The existing draft-composer path takes
      // over from there (PATCH /emails/:id, POST send, DELETE).
      queryClient.invalidateQueries({ queryKey: ['email-thread', selectedThreadId] })
      queryClient.invalidateQueries({ queryKey: ['emails', projectId] })
      queryClient.invalidateQueries({ queryKey: ['email-threads', projectId] })
    },
    onError: (err: Error) => {
      setReplyError(err?.message || (isEs ? 'No se pudo crear la respuesta' : 'Could not create reply'))
    },
  })

  const clearSelectedThread = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('thread')
    setSearchParams(next, { replace: true })
  }

  // Thread status localized labels (bilingual). PR-NEW: CLOSED
  // surfaces as "Archived / Archivado" in user-facing UI to match
  // standard email-service terminology ("Archive" / "Archived"
  // instead of "Close conversation" / "Conversation closed"). The
  // DB column stays "CLOSED" for backward compatibility — only
  // the rendered label changed.
  const threadStatusLabel = (status: string): string => {
    switch (status) {
      case 'DRAFT': return isEs ? 'Borrador' : 'Draft'
      case 'SENT': return isEs ? 'Enviado' : 'Sent'
      case 'REPLIED': return isEs ? 'Respondido' : 'Replied'
      case 'CLOSED': return isEs ? 'Archivado' : 'Archived'
      default: return status
    }
  }
  // Bilingual date formatter for list rows / detail headers.
  const fmtDate = (raw: string | null | undefined): string => {
    if (!raw) return ''
    const d = new Date(raw)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleString(isEs ? 'es-MX' : 'en-US')
  }
  const sourceTypeLabel = (sourceType: string): string => {
    switch (sourceType) {
      case 'MATERIAL_ORDER': return isEs ? 'Pedido de material' : 'Material order'
      case 'QUOTE_REQUEST': return isEs ? 'Cotización' : 'Quote request'
      case 'CONTRACT': return isEs ? 'Contrato' : 'Contract'
      case 'GENERAL': return isEs ? 'General' : 'General'
      default: return sourceType
    }
  }

  return (
    <div className="p-5 max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">{t('Emails')}</h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {drafts.length} {t('drafts')} · {sent.length} {t('sent')}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* PR-NEW: list-level Refresh — the user reported the
              Replies section didn't appear after replying to a
              sent email. Without this button, the only refetch
              triggers were mutation callbacks. Now the user can
              ask the UI to re-check for new inbound replies at
              any time. */}
          <button
            type="button"
            onClick={refreshAllEmails}
            disabled={isRefreshingThreads}
            className="flex items-center gap-1.5 border border-[#ECEAE4] text-[12px] font-[800] text-[#06539B] px-3 py-2 rounded-full hover:bg-[#FAFAF8] disabled:opacity-50"
            aria-label={isEs ? 'Actualizar correos' : 'Refresh emails'}
            title={isEs ? 'Buscar respuestas nuevas' : 'Check for new replies'}
          >
            <RefreshCw size={13} className={isRefreshingThreads ? 'animate-spin' : ''} />
            {isEs ? 'Actualizar' : 'Refresh'}
          </button>
          {/* PR #138B: the Generate Email flow hits the AI endpoint
              and the downstream send hits SendGrid. Both are unsafe
              against a demo project. Hide the entry-point button. */}
          {!isDemo && (
            <button onClick={() => setShowGenerator(true)}
              className="flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full">
              <Mail size={14} /> {t('Generate Email')}
            </button>
          )}
        </div>
      </div>

      {/* PR #138B: demo banner explaining why compose/send/reply/delete
          actions are inert. The demo email list below still renders. */}
      {isDemo && (
        <SavedProjectOnlyNotice
          testId="emails-compose-demo-notice"
          bodyEn="Email actions (compose, AI generate, send, reply, delete) are available in saved projects. Create or open a real project to manage email threads."
          bodyEs="Las acciones de correo (componer, generar con IA, enviar, responder, eliminar) están disponibles en proyectos guardados. Crea o abre un proyecto real para gestionar hilos de correo."
        />
      )}

      {/* Send error banner */}
      {sendError && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
          <span className="text-[11px] font-[700] text-red-700 flex-1">{sendError}</span>
          <button onClick={() => setSendError(null)} className="text-[10px] font-[700] text-red-500">
            {t('Dismiss')}
          </button>
        </div>
      )}

      {/* PR-NEW: delete-draft toast + error banner */}
      {deleteToast && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
          <Check size={14} className="text-emerald-600 flex-shrink-0" />
          <span className="text-[11px] font-[700] text-emerald-700 flex-1">{deleteToast}</span>
        </div>
      )}
      {deleteError && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
          <span className="text-[11px] font-[700] text-red-700 flex-1">
            {isEs ? 'No se pudo eliminar el borrador' : 'Could not delete draft'}
            {deleteError ? ` — ${deleteError}` : ''}
          </span>
          <button onClick={() => setDeleteError(null)} className="text-[10px] font-[700] text-red-500">
            {t('Dismiss')}
          </button>
        </div>
      )}

      {/* PR-NEW: reply-draft creation error banner */}
      {replyError && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
          <span className="text-[11px] font-[700] text-red-700 flex-1">
            {isEs ? 'No se pudo crear la respuesta' : 'Could not create reply'}
            {replyError ? ` — ${replyError}` : ''}
          </span>
          <button onClick={() => setReplyError(null)} className="text-[10px] font-[700] text-red-500">
            {t('Dismiss')}
          </button>
        </div>
      )}

      {/* Email generator */}
      {showGenerator && (
        <EmailGenerator
          projectId={projectId!}
          isEs={isEs}
          onClose={() => setShowGenerator(false)}
          onGenerated={() => { setShowGenerator(false); queryClient.invalidateQueries({ queryKey: ['emails', projectId] }) }}
        />
      )}

      {/* PR-NEW: mailbox tabs — ALWAYS rendered, including when
          a thread detail is open. Previously the strip was hidden
          on the detail view (`{!selectedThreadId && (...)}`),
          which made users feel trapped inside an email with no
          way back to Replies/Drafts/Sent except the "Back to
          emails" button. Real mail clients keep mailbox nav
          visible at all times. Clicking a tab while a detail is
          open closes the detail (clears `?thread=`) and switches
          to the selected mailbox. */}
      <div
        className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1"
        role="tablist"
        aria-label={isEs ? 'Buzones de correo' : 'Email mailboxes'}
      >
        {([
          { key: 'ALL',      en: 'All',      es: 'Todos',      count: allCount },
          { key: 'DRAFTS',   en: 'Drafts',   es: 'Borradores', count: draftsCount },
          { key: 'SENT',     en: 'Sent',     es: 'Enviados',   count: sentCount },
          { key: 'REPLIES',  en: 'Replies',  es: 'Respuestas', count: inboundReplyCount },
          { key: 'ARCHIVED', en: 'Archived', es: 'Archivados', count: archivedCount },
        ] as const).map((tab) => {
          const isActive = !selectedThreadId && activeMailbox === tab.key
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => {
                setActiveMailbox(tab.key)
                if (selectedThreadId) {
                  // Tab clicks from inside a detail behave like
                  // a normal mail client: close the email and
                  // jump to the chosen mailbox.
                  const next = new URLSearchParams(searchParams)
                  next.delete('thread')
                  setSearchParams(next, { replace: true })
                }
              }}
              className={`flex items-center gap-1.5 text-[12px] font-[800] rounded-full px-3.5 py-2 whitespace-nowrap transition-colors ${
                isActive
                  ? 'bg-[#06539B] text-white border border-[#06539B]'
                  : 'bg-white text-[#1C1A17] border border-[#ECEAE4] hover:border-[#06539B]'
              }`}
            >
              <span>{isEs ? tab.es : tab.en}</span>
              <span
                className={`text-[10px] font-[800] rounded-full px-1.5 py-0.5 ${
                  isActive
                    ? 'bg-white/20 text-white'
                    : 'bg-[#F2F0EB] text-[#9E9890]'
                }`}
                aria-label={isEs ? `${tab.count} elementos` : `${tab.count} items`}
              >
                {tab.count}
              </span>
            </button>
          )
        })}
      </div>

      {/* PR-NEW: thread detail (Level 3 reply timeline).
          Opens when ?thread=<id> is set — e.g. Material
          Orders deep-links here after creating a thread. */}
      {selectedThreadId && selectedThread && (
        <div className="bg-white border border-[#ECEAE4] rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#ECEAE4] flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">
                  {sourceTypeLabel(selectedThread.sourceType)}
                </span>
                <span
                  className={`text-[9px] font-[800] uppercase tracking-[0.5px] px-2 py-0.5 rounded-full ${
                    selectedThread.status === 'REPLIED'
                      ? 'bg-emerald-50 text-emerald-700'
                      : selectedThread.status === 'SENT'
                      ? 'bg-[#06539B]/10 text-[#06539B]'
                      : selectedThread.status === 'CLOSED'
                      ? 'bg-[#F2F0EB] text-[#9E9890]'
                      : 'bg-amber-50 text-amber-700'
                  }`}
                >
                  {threadStatusLabel(selectedThread.status)}
                </span>
                {/* F1 — Back-to-source link. Renders ONLY when the
                    thread was opened from a Material Order (the
                    server stamps `sourceType='MATERIAL_ORDER'` +
                    `sourceId=<materialOrderId>` at thread creation
                    in `POST /material-orders/email-thread`). Both
                    fields are already fetched by the existing GET
                    /api/email-threads/:id call — no new API, no
                    new state. Other sourceTypes (QUOTE_REQUEST,
                    CONTRACT, GENERAL) are not yet emitted by any
                    backend code path; the link stays hidden for
                    them until those flows ship. Quiet styling —
                    not a primary CTA. Mirrors the PR #167 pattern
                    used by ContactsModule's back pill. */}
                {selectedThread.sourceType === 'MATERIAL_ORDER' &&
                  selectedThread.sourceId &&
                  projectId && (
                    <Link
                      to={`/projects/${projectId}/material-orders`}
                      data-testid="emails-back-to-material-orders"
                      className="text-[10px] font-[700] text-[#06539B] hover:underline whitespace-nowrap"
                    >
                      {isEs ? '← Volver a Pedidos de Material' : '← Back to Material Orders'}
                    </Link>
                  )}
              </div>
              <div className="text-[13px] font-[800] text-[#1C1A17] mt-1 truncate">
                {selectedThread.subject}
              </div>
              <div className="text-[10px] font-[600] text-[#9E9890] mt-0.5">
                {isEs ? 'Para' : 'To'}:{' '}
                {selectedThread.recipientEmail || (isEs ? 'Sin destinatario' : 'No recipient')}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* PR-NEW: Reply button. Visible when the thread
                  contains at least one INBOUND message. Clicking
                  creates (or resumes) a DRAFT EmailDraft linked
                  to this thread; the FE then refetches the
                  detail and the existing draft-composer panel
                  renders for the user to write + send. */}
              {(() => {
                // PR #138B: demo can never POST a reply draft.
                if (isDemo) return null
                const hasInbound = (selectedThread.messages || []).some((m) => m.direction === 'INBOUND')
                const hasOpenReplyDraft = (selectedThread.drafts || []).some((d) => d.status === 'DRAFT')
                if (!hasInbound || hasOpenReplyDraft) return null
                return (
                  <button
                    type="button"
                    onClick={() => replyDraftMutation.mutate(selectedThread.id)}
                    disabled={replyDraftMutation.isPending}
                    className="inline-flex items-center gap-1.5 text-[11px] font-[800] bg-[#06539B] text-white rounded-full px-3 py-1.5 hover:bg-[#14457E] disabled:opacity-50"
                    aria-label={isEs ? 'Responder' : 'Reply'}
                  >
                    <Send size={11} />
                    {replyDraftMutation.isPending
                      ? (isEs ? 'Preparando…' : 'Preparing…')
                      : (isEs ? 'Responder' : 'Reply')}
                  </button>
                )
              })()}
              <button
                type="button"
                onClick={() => refetchSelectedThread()}
                disabled={isRefreshingThread}
                className="flex items-center gap-1.5 text-[10px] font-[800] border border-[#ECEAE4] text-[#06539B] rounded-full px-3 py-1.5 hover:bg-[#FAFAF8] disabled:opacity-50"
                aria-label={isEs ? 'Actualizar conversación' : 'Refresh conversation'}
                title={isEs ? 'Buscar respuestas nuevas' : 'Check for new replies'}
              >
                <RefreshCw size={11} className={isRefreshingThread ? 'animate-spin' : ''} />
                {isEs ? 'Actualizar' : 'Refresh'}
              </button>
              {/* PR #138B: demo cannot mutate thread state. */}
              {!isDemo && selectedThread.status !== 'CLOSED' && (
                <button
                  type="button"
                  onClick={() => closeThreadMutation.mutate(selectedThread.id)}
                  disabled={closeThreadMutation.isPending}
                  className="text-[10px] font-[800] border border-[#ECEAE4] text-[#9E9890] rounded-full px-3 py-1.5 hover:bg-[#FAFAF8]"
                  aria-label={isEs ? 'Archivar correo' : 'Archive email'}
                  // PR-NEW: hover tooltip clarifies that archive
                  // is non-destructive — users were unsure whether
                  // it deleted sent/replied history.
                  title={isEs
                    ? 'Archivar oculta el correo de la lista principal. No lo elimina.'
                    : 'Archive hides the email from the main list. It does not delete it.'}
                >
                  {isEs ? 'Archivar correo' : 'Archive email'}
                </button>
              )}
              <button
                type="button"
                onClick={clearSelectedThread}
                className="text-[10px] font-[800] text-[#9E9890] hover:text-[#1C1A17] px-2"
                aria-label={isEs ? 'Volver a correos' : 'Back to emails'}
              >
                {isEs ? 'Volver a correos' : 'Back to emails'}
              </button>
            </div>
          </div>

          {/* Timeline */}
          <div className="divide-y divide-[#F2F0EB]">
            {/* DRAFT messages — exposed as editable email cards
                so the user reviews + sends from inside the
                thread. Reuses the existing EmailCard so the
                send/edit plumbing stays in one place. */}
            {(selectedThread.drafts || []).filter((d) => d.status === 'DRAFT').map((d) => {
              // PR-NEW: when the thread already has supplier
              // replies, the draft is functionally a "reply" —
              // surface the composer with "Send reply / Enviar
              // respuesta" copy instead of the generic "Send
              // email". Detected by any INBOUND message in the
              // thread's timeline.
              const threadHasInbound = (selectedThread.messages || []).some(
                (m) => m.direction === 'INBOUND',
              )
              return (
                <div key={d.id} className="px-4 py-3 bg-[#FFFBEB]/40">
                  <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-amber-700 mb-2">
                    {isEs ? 'Borrador' : 'Draft'} · {isEs ? 'Revisa antes de enviar' : 'Review before sending'}
                  </div>
                  <EmailCard
                    email={d}
                    isEs={isEs}
                    onSend={() => sendMutation.mutate(d.id)}
                    onSaveDraft={async (patch) => {
                      await updateMutation.mutateAsync({ emailId: d.id, ...patch })
                    }}
                    onDeleteDraft={() => deleteMutation.mutate(d.id)}
                    isSending={sendMutation.isPending}
                    isDeleting={deleteMutation.isPending}
                    defaultExpanded
                    replyMode={threadHasInbound}
                  />
                </div>
              )
            })}

            {/* OUTBOUND + INBOUND audit timeline.
                PR-NEW: every row now shows explicit From / To
                labels and a prefixed Sent on / Reply received on
                date — same vocabulary a normal mail client uses.
                The status badge differentiates an OUTBOUND that
                follows an INBOUND ("Reply sent / Respuesta
                enviada") from the original send ("Sent / Enviado"),
                so the thread reads chronologically. */}
            {(selectedThread.messages || []).map((m, idx, all) => {
              const isInbound = m.direction === 'INBOUND'
              // A "reply sent" is an OUTBOUND message that has
              // any earlier INBOUND in the same thread.
              const isReplySent =
                !isInbound &&
                all.slice(0, idx).some((prev) => prev.direction === 'INBOUND')
              const statusLabel = isInbound
                ? (isEs ? 'El proveedor respondió' : 'Supplier replied')
                : isReplySent
                ? (isEs ? 'Respuesta enviada' : 'Reply sent')
                : (isEs ? 'Enviado' : 'Sent')
              const fromLabel = isInbound
                ? m.fromEmail
                : (isEs ? 'NATAMI' : 'NATAMI')
              const toLabel = isInbound
                ? (m.toEmail || '')
                : (m.toEmail || '')
              const stampStr = (() => {
                const stamp = isInbound ? m.receivedAt : m.sentAt
                if (!stamp) return ''
                const d = new Date(stamp)
                if (Number.isNaN(d.getTime())) return ''
                return d.toLocaleString(isEs ? 'es-MX' : 'en-US')
              })()
              const datePrefix = stampStr
                ? (isInbound
                    ? (isEs ? `Respuesta recibida el ${stampStr}` : `Reply received on ${stampStr}`)
                    : (isEs ? `Enviado el ${stampStr}` : `Sent on ${stampStr}`))
                : ''
              return (
                <div key={m.id} className={`px-4 py-3 ${isInbound ? 'bg-emerald-50/30' : ''}`}>
                  <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span
                        className={`text-[9px] font-[800] uppercase tracking-[0.5px] px-2 py-0.5 rounded-full ${
                          isInbound
                            ? 'bg-emerald-100 text-emerald-700'
                            : isReplySent
                            ? 'bg-[#06539B]/10 text-[#06539B]'
                            : 'bg-[#06539B]/10 text-[#06539B]'
                        }`}
                      >
                        {statusLabel}
                      </span>
                    </div>
                    {datePrefix && (
                      <div className="text-[10px] font-[700] text-[#9E9890]">{datePrefix}</div>
                    )}
                  </div>
                  {/* From / To header rows — professional mail look. */}
                  <div className="text-[10px] font-[600] text-[#9E9890] mb-0.5">
                    <span className="font-[800] text-[#1C1A17]">{isEs ? 'De' : 'From'}: </span>
                    {fromLabel}
                  </div>
                  {toLabel && (
                    <div className="text-[10px] font-[600] text-[#9E9890] mb-1">
                      <span className="font-[800] text-[#1C1A17]">{isEs ? 'Para' : 'To'}: </span>
                      {toLabel}
                    </div>
                  )}
                  {m.subject && (
                    <div className="text-[12px] font-[800] text-[#1C1A17] mb-1">
                      <span className="font-[800] text-[#9E9890] text-[10px] mr-1">{isEs ? 'Asunto:' : 'Subject:'}</span>
                      {m.subject}
                    </div>
                  )}
                  {/* PR-NEW: HTML-only inbound rows had empty
                      textBody on the server, which rendered as
                      "(no content)". The inbound parser now
                      derives plain text from htmlBody on insert
                      (deterministic tag strip); this FE block
                      ALSO falls back for legacy rows that were
                      written before that fix landed. The empty
                      placeholder uses the spec-required bilingual
                      copy. */}
                  {(() => {
                    const displayBody = (() => {
                      if (m.textBody && m.textBody.trim()) return m.textBody
                      if (m.htmlBody && m.htmlBody.trim()) {
                        return String(m.htmlBody)
                          .replace(/<style[\s\S]*?<\/style>/gi, ' ')
                          .replace(/<script[\s\S]*?<\/script>/gi, ' ')
                          .replace(/<br\s*\/?>/gi, '\n')
                          .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, '\n')
                          .replace(/<[^>]+>/g, '')
                          .replace(/&nbsp;/g, ' ')
                          .replace(/&amp;/g, '&')
                          .replace(/&lt;/g, '<')
                          .replace(/&gt;/g, '>')
                          .replace(/&quot;/g, '"')
                          .replace(/&#39;/g, "'")
                          .replace(/[ \t]+\n/g, '\n')
                          .replace(/\n{3,}/g, '\n\n')
                          .trim()
                      }
                      return ''
                    })()
                    if (displayBody) {
                      return (
                        <pre className="text-[11px] font-[600] text-[#1C1A17] whitespace-pre-wrap font-sans">
                          {displayBody}
                        </pre>
                      )
                    }
                    return (
                      <div className="text-[11px] font-[600] text-[#9E9890] italic">
                        {isEs ? 'No se recibió contenido del mensaje.' : 'No message body received.'}
                      </div>
                    )
                  })()}
                  {isInbound && (() => {
                    // Section A.7 of the product spec lists both
                    // "Reply received / Respuesta recibida" AND
                    // "Supplier replied / El proveedor respondió"
                    // as required Emails strings. The badge above
                    // carries the latter; this footer carries the
                    // former plus the received-on datetime so the
                    // user sees both phrasings.
                    const stamp = (() => {
                      if (!m.receivedAt) return ''
                      const d = new Date(m.receivedAt)
                      return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(isEs ? 'es-MX' : 'en-US')
                    })()
                    return (
                      <div className="text-[9px] font-[700] text-emerald-700 mt-1">
                        {isEs ? 'Respuesta recibida' : 'Reply received'}
                        {stamp && <span className="font-[600]"> · {stamp}</span>}
                      </div>
                    )
                  })()}
                </div>
              )
            })}

            {(selectedThread.messages || []).length === 0 &&
              (selectedThread.drafts || []).filter((d) => d.status === 'DRAFT').length === 0 && (
              <div className="px-4 py-6 text-[11px] font-[600] text-[#9E9890] text-center">
                {isEs ? 'Aún no hay mensajes en este correo.' : 'No messages in this email yet.'}
              </div>
            )}

            {/* Waiting-for-reply indicator when the conversation
                is SENT but no INBOUND row exists yet. Includes the
                Sent-on datetime from the most recent OUTBOUND row
                so the user has something concrete to anchor to. */}
            {(() => {
              const hasInbound = (selectedThread.messages || []).some((m) => m.direction === 'INBOUND')
              const lastOutbound = [...(selectedThread.messages || [])]
                .filter((m) => m.direction === 'OUTBOUND')
                .sort((a, b) => {
                  const ta = new Date(a.sentAt || a.createdAt).getTime()
                  const tb = new Date(b.sentAt || b.createdAt).getTime()
                  return tb - ta
                })[0]
              const sentStamp = lastOutbound?.sentAt || lastOutbound?.createdAt || null
              const sentStr = (() => {
                if (!sentStamp) return ''
                const d = new Date(sentStamp)
                return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(isEs ? 'es-MX' : 'en-US')
              })()
              if (selectedThread.status === 'SENT' && !hasInbound) {
                return (
                  <div className="px-4 py-2 text-[10px] font-[700] text-[#9E9890] italic">
                    {isEs ? 'Esperando respuesta del proveedor…' : 'Waiting for supplier reply…'}
                    {sentStr && (
                      <span className="not-italic font-[600] text-[#9E9890] ml-2">
                        · {isEs ? `Enviado el ${sentStr}` : `Sent on ${sentStr}`}
                      </span>
                    )}
                  </div>
                )
              }
              if (selectedThread.status === 'REPLIED') {
                return (
                  <div className="px-4 py-2 text-[10px] font-[700] text-emerald-700">
                    {isEs ? 'El proveedor respondió' : 'Supplier replied'}
                  </div>
                )
              }
              if (selectedThread.status === 'CLOSED') {
                return (
                  <div className="px-4 py-2 text-[10px] font-[700] text-[#9E9890]">
                    {isEs ? 'Correo archivado' : 'Archived email'}
                  </div>
                )
              }
              return null
            })()}
          </div>

          {/* Per-conversation reply address. Friendly first, then
              the technical address as small metadata. The address
              must stay visible because it's what the supplier replied
              to — surfacing it helps debug bounce/spam issues. */}
          {selectedThread.replyAddress && (
            <div className="px-4 py-2 border-t border-[#ECEAE4]">
              <div className="text-[10px] font-[700] text-[#1C1A17]">
                {isEs
                  ? 'Las respuestas del proveedor aparecerán automáticamente en este correo.'
                  : 'Supplier replies will appear automatically in this email.'}
              </div>
              <div className="text-[9px] font-[600] text-[#9E9890] mt-0.5">
                {isEs ? 'Dirección técnica de respuesta' : 'Technical reply address'}:{' '}
                <code className="font-mono text-[9px] text-[#9E9890] break-all">{selectedThread.replyAddress}</code>
              </div>
            </div>
          )}

          {/* PR-NEW: when the thread was created without a
              per-thread Reply-To (INBOUND_EMAIL_DOMAIN was unset
              at the time, or buildReplyToAddress refused), the
              outbound email had no Reply-To header and supplier
              replies cannot be captured by /api/email/inbound.
              The thread is effectively a one-way send. Surface
              this clearly so the user understands why the reply
              they sent is not appearing in NATAMI. */}
          {!selectedThread.replyAddress &&
            (selectedThread.status === 'SENT' || selectedThread.status === 'REPLIED') && (
            <div className="px-4 py-3 border-t border-amber-200 bg-amber-50">
              <div className="flex items-start gap-2">
                <AlertCircle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-[11px] font-[800] text-amber-800">
                    {isEs
                      ? 'Las respuestas del proveedor no se podrán capturar para este correo'
                      : 'Supplier replies cannot be captured for this email'}
                  </div>
                  <div className="text-[10px] font-[600] text-amber-700 mt-0.5">
                    {isEs
                      ? 'Este correo se envió sin dirección de respuesta única. Crea un correo nuevo para que las respuestas aparezcan automáticamente en NATAMI.'
                      : 'This email was sent without a per-thread reply address. Create a new email so future supplier replies appear automatically in NATAMI.'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* PR-NEW: email-service section structure.
          Drafts / Sent / Replies / Archived.
          The previous "Conversations" thread list was removed —
          users didn't recognize that vocabulary. Threads are now
          surfaced indirectly via the Replies (INBOUND messages)
          and Archived (CLOSED threads) sections. */}

      {/* Drafts / Borradores */}
      {!selectedThreadId && (activeMailbox === 'ALL' || activeMailbox === 'DRAFTS') && (
        <div>
          <h3 className="text-[12px] font-[800] text-[#06539B] uppercase tracking-[0.5px] mb-2">
            {isEs ? 'Borradores' : 'Drafts'}
          </h3>
          {drafts.length === 0 ? (
            // Empty state shows on the Drafts tab; on All tab the
            // empty Drafts block is also rendered so the user
            // sees the structure consistently.
            <div
              className="bg-[#FAFAF8] border border-dashed border-[#ECEAE4] rounded-xl px-4 py-6 text-center"
              role="status"
              aria-label={isEs ? 'No hay borradores' : 'No drafts'}
            >
              <div className="text-[12px] font-[800] text-[#1C1A17]">
                {isEs ? 'No hay borradores' : 'No drafts yet'}
              </div>
              <div className="text-[11px] font-[600] text-[#9E9890] mt-1">
                {isEs ? 'Los correos sin enviar aparecerán aquí.' : 'Unsent emails will appear here.'}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {drafts.map(email => (
                <EmailCard key={email.id} email={email} isEs={isEs}
                  onSend={() => sendMutation.mutate(email.id)}
                  onSaveDraft={async (patch) => {
                    await updateMutation.mutateAsync({ emailId: email.id, ...patch })
                  }}
                  onDeleteDraft={() => deleteMutation.mutate(email.id)}
                  isSending={sendMutation.isPending}
                  isDeleting={deleteMutation.isPending} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sent / Enviados */}
      {!selectedThreadId && (activeMailbox === 'ALL' || activeMailbox === 'SENT') && (
        <div>
          <h3 className="text-[12px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-2">
            {isEs ? 'Enviados' : 'Sent'}
          </h3>
          {sent.length === 0 ? (
            <div
              className="bg-[#FAFAF8] border border-dashed border-[#ECEAE4] rounded-xl px-4 py-6 text-center"
              role="status"
              aria-label={isEs ? 'No hay correos enviados' : 'No sent emails'}
            >
              <div className="text-[12px] font-[800] text-[#1C1A17]">
                {isEs ? 'Aún no hay correos enviados' : 'No sent emails yet'}
              </div>
              <div className="text-[11px] font-[600] text-[#9E9890] mt-1">
                {isEs ? 'Los correos enviados aparecerán aquí.' : 'Sent emails will appear here.'}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {sent.map(email => {
                // PR-NEW: link the SENT EmailCard back to its
                // thread when a supplier reply has landed. Without
                // this, the user opens the SENT card, sees only the
                // outbound body, and has no clue that a reply is
                // available one tab over.
                const linkedThread = email.threadId
                  ? threads.find((th) => th.id === email.threadId)
                  : null
                const onOpenReply =
                  linkedThread && linkedThread.status === 'REPLIED'
                    ? () => {
                        const next = new URLSearchParams(searchParams)
                        next.set('thread', linkedThread.id)
                        setSearchParams(next, { replace: false })
                      }
                    : undefined
                return (
                  <EmailCard
                    key={email.id}
                    email={email}
                    isEs={isEs}
                    onOpenReply={onOpenReply}
                  />
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* PR-NEW: Replies / Respuestas — ALWAYS rendered when no
          thread is selected, even with zero inbound rows.
          Previously the section was hidden when empty, leaving
          the user with no permanent place to look for supplier
          replies. The visible empty state confirms "replies will
          land here" and reduces the "where did my reply go?"
          confusion. */}
      {!selectedThreadId && (activeMailbox === 'ALL' || activeMailbox === 'REPLIES') && (() => {
        const replyRows = threads
          .filter((th) => th.status !== 'CLOSED')
          .flatMap((th) =>
            (th.messages || [])
              .filter((m) => m.direction === 'INBOUND')
              .map((m) => ({ thread: th, message: m })),
          )
          .sort((a, b) => {
            const ta = new Date(a.message.receivedAt || a.message.createdAt).getTime()
            const tb = new Date(b.message.receivedAt || b.message.createdAt).getTime()
            return tb - ta
          })
        return (
          <div>
            <h3 className="text-[12px] font-[800] text-emerald-700 uppercase tracking-[0.5px] mb-2">
              {isEs ? 'Respuestas' : 'Replies'}
            </h3>
            {replyRows.length === 0 ? (
              <div
                className="bg-emerald-50/40 border border-dashed border-emerald-200 rounded-xl px-4 py-6 text-center"
                role="status"
                aria-label={isEs ? 'Aún no hay respuestas' : 'No replies yet'}
              >
                <div className="text-[12px] font-[800] text-emerald-800">
                  {isEs ? 'Aún no hay respuestas' : 'No replies yet'}
                </div>
                <div className="text-[11px] font-[600] text-emerald-700 mt-1">
                  {isEs
                    ? 'Las respuestas de proveedores aparecerán aquí.'
                    : 'Supplier replies will appear here.'}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {replyRows.map(({ thread: th, message: m }) => {
                  const next = new URLSearchParams(searchParams)
                  next.set('thread', th.id)
                  const stamp = fmtDate(m.receivedAt || m.createdAt)
                  const preview = (m.textBody || '').replace(/\s+/g, ' ').trim().slice(0, 140)
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setSearchParams(next, { replace: false })}
                      className="w-full text-left bg-white border border-emerald-200 rounded-xl px-4 py-3 hover:border-emerald-400 transition-colors"
                      aria-label={isEs ? `Abrir respuesta: ${m.subject || th.subject}` : `Open reply: ${m.subject || th.subject}`}
                    >
                      <div className="text-[10px] font-[700] text-emerald-700">
                        {isEs ? `Respuesta recibida el ${stamp}` : `Reply received on ${stamp}`}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap mt-0.5">
                        <span className="text-[9px] font-[800] uppercase tracking-[0.5px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                          {isEs ? 'El proveedor respondió' : 'Supplier replied'}
                        </span>
                        <span className="text-[10px] font-[600] text-[#9E9890] truncate">{m.fromEmail}</span>
                      </div>
                      <div className="text-[13px] font-[800] text-[#1C1A17] truncate mt-0.5">
                        {m.subject || th.subject}
                      </div>
                      {preview && (
                        <div className="text-[11px] font-[600] text-[#9E9890] mt-0.5 line-clamp-2">{preview}</div>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )
      })()}

      {/* PR-NEW: Archived / Archivados.
          CLOSED threads. The detail panel preserves the full
          timeline (outbound + inbound) for archived items —
          archiving is presentational, not destructive. */}
      {!selectedThreadId && (activeMailbox === 'ALL' || activeMailbox === 'ARCHIVED') && (() => {
        const archived = threads.filter((th) => th.status === 'CLOSED')
        if (archived.length === 0) {
          return (
            <div>
              <h3 className="text-[12px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-2">
                {isEs ? 'Archivados' : 'Archived'}
              </h3>
              <div
                className="bg-[#FAFAF8] border border-dashed border-[#ECEAE4] rounded-xl px-4 py-6 text-center"
                role="status"
                aria-label={isEs ? 'No hay correos archivados' : 'No archived emails'}
              >
                <div className="text-[12px] font-[800] text-[#1C1A17]">
                  {isEs ? 'No hay correos archivados' : 'No archived emails yet'}
                </div>
                <div className="text-[11px] font-[600] text-[#9E9890] mt-1">
                  {isEs ? 'Los correos archivados aparecerán aquí.' : 'Archived emails will appear here.'}
                </div>
              </div>
            </div>
          )
        }
        return (
          <div>
            <h3 className="text-[12px] font-[800] text-[#9E9890] uppercase tracking-[0.5px] mb-2">
              {isEs ? 'Archivados' : 'Archived'}
            </h3>
            <div className="space-y-2">
              {archived.map((th) => {
                const next = new URLSearchParams(searchParams)
                next.set('thread', th.id)
                const stamp = fmtDate(th.closedAt || th.updatedAt)
                const hasInbound = (th.messages || []).some((m) => m.direction === 'INBOUND')
                const outboundCount = (th.messages || []).filter((m) => m.direction === 'OUTBOUND').length
                const inboundCount = (th.messages || []).filter((m) => m.direction === 'INBOUND').length
                return (
                  <button
                    key={th.id}
                    type="button"
                    onClick={() => setSearchParams(next, { replace: false })}
                    className="w-full text-left bg-[#FAFAF8] border border-[#ECEAE4] rounded-xl px-4 py-3 hover:border-[#9E9890] transition-colors"
                    aria-label={isEs ? `Abrir correo archivado: ${th.subject}` : `Open archived email: ${th.subject}`}
                  >
                    <div className="text-[10px] font-[700] text-[#9E9890]">
                      {isEs ? `Archivado el ${stamp}` : `Archived on ${stamp}`}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mt-0.5">
                      <span className="text-[9px] font-[800] uppercase tracking-[0.5px] px-2 py-0.5 rounded-full bg-[#F2F0EB] text-[#9E9890]">
                        {isEs ? 'Archivado' : 'Archived'}
                      </span>
                      {hasInbound && (
                        <span className="text-[9px] font-[700] text-emerald-700">
                          {isEs ? 'Incluye respuesta del proveedor' : 'Includes supplier reply'}
                        </span>
                      )}
                    </div>
                    <div className="text-[13px] font-[800] text-[#1C1A17] truncate mt-0.5">{th.subject}</div>
                    <div className="text-[10px] font-[600] text-[#9E9890]">
                      {isEs
                        ? `${outboundCount} enviado(s) · ${inboundCount} respuesta(s)`
                        : `${outboundCount} sent · ${inboundCount} reply(ies)`}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        )
      })()}

      {(!emails || emails.length === 0) && !isLoading && !showGenerator && (
        <div className="text-center py-12">
          <Mail size={36} className="mx-auto text-[#ECEAE4] mb-3" />
          <p className="text-[12px] font-[700] text-[#9E9890]">{t('No emails yet')}</p>
          <p className="text-[10px] font-[600] text-[#9E9890] mt-1">{t('Generate AI emails for orders, quotes, or reminders')}</p>
        </div>
      )}
    </div>
  )
}

// ── EMAIL CARD ────────────────────────────────────
//
// PR-NEW: when status === 'DRAFT', the card renders a proper
// composer (To / Subject / Message inputs) instead of the
// previous static preview. The user can edit all three fields,
// click "Save draft" to persist, and "Send email" to deliver.
// Send always saves the latest edited values first, then sends,
// so stale originals are never delivered.
// When status === 'SENT' / 'REPLIED', the card collapses to a
// readonly summary identical to the prior visual.

function EmailCard({ email, isEs, onSend, onSaveDraft, onDeleteDraft, onOpenReply, isSending, isDeleting, defaultExpanded, replyMode }: {
  email: EmailDraft
  isEs: boolean
  onSend?: () => void
  /** PR-NEW: PATCHes /api/emails/:id with any subset of {to,subject,body} */
  onSaveDraft?: (patch: { to?: string | null; subject?: string; body?: string }) => Promise<void>
  /** PR-NEW: DELETEs /api/emails/:id when the user confirms */
  onDeleteDraft?: () => void
  /** PR-NEW: when the SENT card's thread is REPLIED, opens the
   *  related thread detail so the user can read the supplier reply. */
  onOpenReply?: () => void
  isSending?: boolean
  isDeleting?: boolean
  /** PR-NEW: open the composer by default — used inside the thread detail */
  defaultExpanded?: boolean
  /** PR-NEW: when true, the primary button reads
   *  "Send reply / Enviar respuesta" instead of "Send email /
   *  Enviar correo". Used by the reply-draft composer inside a
   *  thread that already has supplier replies. */
  replyMode?: boolean
}) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(!!defaultExpanded)
  const typeInfo = EMAIL_TYPES[email.type]
  const TypeIcon = typeInfo?.icon || Mail
  const isDraft = email.status === 'DRAFT'

  // PR-NEW: local composer state mirrors the server-side draft.
  // Reset whenever the underlying email row changes (e.g. after
  // a Save invalidation refreshes the cache + re-renders the
  // parent with a fresh `email` prop).
  const [draftTo, setDraftTo] = useState(email.to || '')
  const [draftSubject, setDraftSubject] = useState(email.subject || '')
  const [draftBody, setDraftBody] = useState(email.body || '')
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [saveError, setSaveError] = useState<string | null>(null)
  // PR-NEW: inline two-step delete confirmation. First click swaps
  // the Delete button for a localized "Delete this draft? …
  // [Confirm] [Cancel]" affordance; second click on Confirm fires
  // onDeleteDraft. window.confirm was avoided because it can't be
  // localized cleanly and breaks the bilingual contract.
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setDraftTo(email.to || '')
    setDraftSubject(email.subject || '')
    setDraftBody(email.body || '')
    setSaveState('idle')
    setSaveError(null)
    setConfirmingDelete(false)
  }, [email.id, email.to, email.subject, email.body, email.status])

  const isDirty =
    (draftTo || '').trim() !== (email.to || '').trim() ||
    (draftSubject || '') !== (email.subject || '') ||
    (draftBody || '') !== (email.body || '')
  const hasRecipient = !!(draftTo || '').trim()

  const handleSaveDraft = async (): Promise<boolean> => {
    if (!onSaveDraft || !isDraft) return true
    if (!isDirty) return true
    setSaveState('saving')
    setSaveError(null)
    try {
      await onSaveDraft({
        to: (draftTo || '').trim() || null,
        subject: draftSubject,
        body: draftBody,
      })
      setSaveState('saved')
      // Reset "saved" toast after a short delay so the user
      // doesn't see it forever.
      setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 2500)
      return true
    } catch (err: any) {
      setSaveState('error')
      setSaveError(err?.message || (isEs ? 'No se pudo guardar el borrador' : 'Could not save draft'))
      return false
    }
  }

  const handleSend = async () => {
    if (!onSend) return
    if (!hasRecipient) return
    // PR-NEW: save the latest edits BEFORE handing off to the send
    // mutation. If saving fails we abort the send so the user
    // doesn't deliver a stale version. The send mutation reads
    // from the server, so this guarantees the server has the
    // latest values when it runs.
    if (isDirty) {
      const ok = await handleSaveDraft()
      if (!ok) return
    }
    onSend()
  }

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
      <div
        onClick={() => setExpanded(!expanded)}
        className="p-3 cursor-pointer hover:bg-[#FAFAF8] transition-colors"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setExpanded(!expanded)
          }
        }}
        aria-expanded={expanded}
        aria-label={
          expanded
            ? (isEs ? 'Ocultar detalles' : 'Hide details')
            : (isEs ? 'Mostrar detalles' : 'Show details')
        }
      >
        <div className="flex items-center gap-3">
          <TypeIcon size={16} className="text-[#9E9890] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            {/* PR-NEW: list-level date line. SENT rows show
                "Sent on …" / "Enviado el …"; DRAFT rows show
                "Draft updated …" / "Borrador actualizado …".
                This makes the list scannable like a normal mail
                client without forcing the user to open each row. */}
            {(() => {
              const stamp = (() => {
                const raw = email.status === 'SENT'
                  ? email.sentAt
                  : (email.updatedAt || email.createdAt)
                if (!raw) return ''
                const d = new Date(raw)
                return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(isEs ? 'es-MX' : 'en-US')
              })()
              if (!stamp) return null
              return (
                <div className="text-[10px] font-[700] text-[#9E9890]">
                  {email.status === 'SENT'
                    ? (isEs ? `Enviado el ${stamp}` : `Sent on ${stamp}`)
                    : (isEs ? `Borrador actualizado ${stamp}` : `Draft updated ${stamp}`)}
                </div>
              )
            })()}
            <div className="text-[12px] font-[700] text-[#1C1A17] truncate">{email.subject}</div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[9px] font-[700] text-[#9E9890]">
                {isEs ? typeInfo?.es : typeInfo?.en || email.type}
              </span>
              {email.to
                ? <span className="text-[9px] font-[600] text-[#9E9890]">{email.to}</span>
                : isDraft && <span className="text-[9px] font-[700] text-amber-500">{isEs ? 'Sin destinatario' : 'No recipient'}</span>
              }
            </div>
          </div>
          <span className={`text-[9px] font-[800] px-2.5 py-1 rounded-full ${email.status === 'SENT' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>
            {email.status === 'SENT' ? t('Sent') : t('Draft')}
          </span>
        </div>
      </div>

      {expanded && isDraft && (
        <div className="px-4 pb-4 border-t border-[#ECEAE4] pt-3 space-y-3">
          {/* Draft banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <div className="text-[11px] font-[800] text-amber-800">
              {isEs ? 'Borrador de correo — revisa y edita antes de enviar' : 'Draft email — review and edit before sending'}
            </div>
            <div className="text-[10px] font-[600] text-amber-700 mt-0.5">
              {isEs
                ? 'Agrega notas de entrega, alternativas, instrucciones de flete/impuestos o solicitudes especiales antes de enviar.'
                : 'Add delivery notes, alternates, freight/tax instructions, or special requests before sending.'}
            </div>
          </div>

          {/* Composer */}
          <div className="space-y-2">
            <div className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#06539B]">
              {isEs ? 'Editar correo' : 'Edit email'}
            </div>

            <label className="block">
              <span className="text-[10px] font-[800] text-[#9E9890] block mb-1">{isEs ? 'Para' : 'To'}</span>
              <input
                type="email"
                value={draftTo}
                onChange={(e) => setDraftTo(e.target.value)}
                placeholder="email@example.com"
                className={`w-full border rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B] ${hasRecipient ? 'border-[#ECEAE4]' : 'border-amber-300 bg-amber-50'}`}
                aria-label={isEs ? 'Para' : 'To'}
              />
            </label>

            <label className="block">
              <span className="text-[10px] font-[800] text-[#9E9890] block mb-1">{isEs ? 'Asunto' : 'Subject'}</span>
              <input
                type="text"
                value={draftSubject}
                onChange={(e) => setDraftSubject(e.target.value)}
                className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
                aria-label={isEs ? 'Asunto' : 'Subject'}
              />
            </label>

            <label className="block">
              <span className="text-[10px] font-[800] text-[#9E9890] block mb-1">{isEs ? 'Mensaje' : 'Message'}</span>
              <textarea
                value={draftBody}
                onChange={(e) => setDraftBody(e.target.value)}
                className="email-composer-message w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] text-[#1C1A17] leading-relaxed focus:outline-none focus:border-[#06539B] bg-white whitespace-pre-wrap resize-y min-h-[220px] md:min-h-[260px]"
                aria-label={isEs ? 'Mensaje' : 'Message'}
              />
            </label>
          </div>

          {/* PR-NEW: Save status feedback. Now rendered as a
              full-width banner with an icon so the user actually
              notices that Save did something. Earlier 10px text
              flash was easy to miss — root cause of the production
              "Save draft does nothing" report.
              When idle + no changes, show a subtle helper so the
              user understands why the button is disabled. */}
          {saveState === 'saving' && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-[12px] font-[700] text-amber-800">
              <Loader2 size={12} className="animate-spin" />
              {isEs ? 'Guardando…' : 'Saving…'}
            </div>
          )}
          {saveState === 'saved' && (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-[12px] font-[700] text-emerald-800">
              <Check size={12} />
              {isEs ? 'Borrador guardado' : 'Draft saved'}
            </div>
          )}
          {saveState === 'error' && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-[12px] font-[700] text-red-700">
              <AlertCircle size={12} />
              {saveError || (isEs ? 'No se pudo guardar el borrador' : 'Could not save draft')}
            </div>
          )}
          {saveState === 'idle' && !isDirty && onSaveDraft && (
            <div className="text-[10px] font-[600] text-[#9E9890] italic">
              {isEs ? 'No hay cambios para guardar' : 'No changes to save'}
            </div>
          )}

          {/* Action bar — clearly separated from message body */}
          <div className="flex flex-wrap items-center gap-2 border-t border-[#F2F0EB] pt-3">
            {/* Delete draft — destructive secondary, leftmost so it's
                not crowded next to Send. Uses inline two-step confirm
                instead of window.confirm so copy stays bilingual. */}
            {onDeleteDraft && (
              confirmingDelete ? (
                <div className="flex flex-wrap items-center gap-2 flex-1">
                  <span className="text-[10px] font-[700] text-red-700">
                    {isEs
                      ? '¿Eliminar este borrador? Esta acción no se puede deshacer.'
                      : 'Delete this draft? This cannot be undone.'}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setConfirmingDelete(false); onDeleteDraft() }}
                    disabled={isDeleting}
                    className="text-[10px] font-[800] bg-red-600 text-white rounded-full px-3 py-1.5 hover:bg-red-700 disabled:opacity-50"
                    aria-label={isEs ? 'Confirmar eliminación' : 'Confirm delete'}
                  >
                    {isDeleting
                      ? (isEs ? 'Eliminando…' : 'Deleting…')
                      : (isEs ? 'Confirmar' : 'Confirm')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingDelete(false)}
                    disabled={isDeleting}
                    className="text-[10px] font-[700] text-[#9E9890] rounded-full px-2 py-1.5 hover:text-[#1C1A17] disabled:opacity-50"
                  >
                    {isEs ? 'Cancelar' : 'Cancel'}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(true)}
                  disabled={saveState === 'saving' || isSending || isDeleting}
                  className="text-[11px] font-[800] border border-red-200 text-red-600 rounded-full px-4 py-2 hover:bg-red-50 disabled:opacity-50"
                  aria-label={isEs ? 'Eliminar borrador' : 'Delete draft'}
                >
                  {isEs ? 'Eliminar borrador' : 'Delete draft'}
                </button>
              )
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={saveState === 'saving' || !isDirty || isDeleting}
              title={!isDirty ? (isEs ? 'No hay cambios para guardar' : 'No changes to save') : undefined}
              className="text-[11px] font-[800] border border-[#ECEAE4] text-[#1C1A17] rounded-full px-4 py-2 hover:bg-[#FAFAF8] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saveState === 'saving'
                ? (isEs ? 'Guardando…' : 'Saving…')
                : (isEs ? 'Guardar borrador' : 'Save draft')}
            </button>
            {onSend && (
              hasRecipient ? (
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={isSending || saveState === 'saving' || isDeleting}
                  className="inline-flex items-center gap-1.5 bg-[#06539B] text-white text-[12px] font-[800] px-4 py-2 rounded-full hover:bg-[#14457E] disabled:opacity-50"
                >
                  <Send size={12} />
                  {isSending
                    ? (isEs ? 'Enviando…' : 'Sending…')
                    : replyMode
                    ? (isEs ? 'Enviar respuesta' : 'Send reply')
                    : (isEs ? 'Enviar correo' : 'Send email')}
                </button>
              ) : (
                <span className="text-[10px] font-[700] text-amber-600">
                  {isEs ? 'Agrega un destinatario antes de enviar' : 'Add a recipient before sending'}
                </span>
              )
            )}
          </div>

          {/* Hide details */}
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="block w-full text-center text-[10px] font-[700] text-[#9E9890] hover:text-[#1C1A17] py-1"
          >
            {isEs ? 'Ocultar detalles' : 'Hide details'}
          </button>
        </div>
      )}

      {expanded && !isDraft && (
        <div className="px-4 pb-4 border-t border-[#ECEAE4] pt-3 space-y-3">
          {/* Readonly preview for SENT / REPLIED rows. */}
          <div className="bg-[#FAFAF8] rounded-lg p-3 text-[11px] font-[600] text-[#1C1A17] whitespace-pre-wrap leading-relaxed max-h-[200px] overflow-y-auto">
            {email.body}
          </div>

          {/* PR-NEW: when the underlying thread has a supplier
              reply, surface it inline so the user doesn't have to
              hunt for it in the Replies section. Click navigates
              to the thread detail where the full inbound message
              is rendered. */}
          {onOpenReply && (
            <button
              type="button"
              onClick={onOpenReply}
              className="w-full flex items-center justify-center gap-1.5 bg-emerald-600 text-white text-[12px] font-[800] px-4 py-2 rounded-full hover:bg-emerald-700"
              aria-label={isEs ? 'Ver respuesta del proveedor' : 'View supplier reply'}
            >
              <MessageSquare size={12} />
              {isEs ? 'Ver respuesta del proveedor' : 'View supplier reply'}
            </button>
          )}

          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="block w-full text-center text-[10px] font-[700] text-[#9E9890] hover:text-[#1C1A17] py-1"
          >
            {isEs ? 'Ocultar detalles' : 'Hide details'}
          </button>
        </div>
      )}
    </div>
  )
}

// ── EMAIL GENERATOR ───────────────────────────────

interface TakeoffItem {
  id: string; name: string; quantity: number; unit: string; category: string
}

export function EmailGenerateErrorNotice({ isEs, onDismiss }: {
  isEs: boolean; onDismiss: () => void
}) {
  return (
    <div role="alert" data-testid="email-generate-error" className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-[11px] font-[700] text-red-700">
      <span className="flex-1 min-w-0 break-words">
        {isEs ? 'No se pudo generar el correo.' : 'Could not generate the email.'}
      </span>
      <button type="button" onClick={onDismiss} aria-label={isEs ? 'Cerrar' : 'Dismiss'} className="text-red-700 flex-shrink-0">×</button>
    </div>
  )
}

function EmailGenerator({ projectId, isEs, onClose, onGenerated }: {
  projectId: string; isEs: boolean; onClose: () => void; onGenerated: () => void
}) {
  const { getToken } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  // PR-NEW: default to the first selectable type. Material
  // orders are no longer selectable here — they live in the
  // Material Orders module.
  const [type, setType] = useState('quote_request')
  const [context, setContext] = useState('')
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState(false)
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  const [expandedCat, setExpandedCat] = useState<string | null>(null)
  const [contactId, setContactId] = useState('')
  const [manualEmail, setManualEmail] = useState('')

  // PR-NEW: only quote_request still needs takeoff items in the
  // selector. Material orders moved to the Material Orders module.
  const needsItems = type === 'quote_request'

  // Fetch contacts for recipient selector
  const { data: contacts } = useQuery<Contact[]>({
    queryKey: ['contacts', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/contacts`, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      })
      if (!res.ok) return []
      return res.json()
    },
  })

  const contactsWithEmail = contacts?.filter(c => c.email) || []
  // Group contacts: SUPPLIER first (Material Orders), then rest by role
  const supplierContacts = contactsWithEmail.filter(c => c.role === 'SUPPLIER')
  const otherContacts = contactsWithEmail.filter(c => c.role !== 'SUPPLIER')

  const { data: takeoffItems } = useQuery<TakeoffItem[]>({
    queryKey: ['takeoff-items-for-email', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/takeoff`, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      })
      if (!res.ok) return []
      const takeoffs = await res.json()
      const latest = takeoffs.find((t: any) => t.status === 'NEEDS_REVIEW' || t.status === 'CONFIRMED' || t.status === 'APPROVED')
      if (!latest) return []
      const statusRes = await apiFetch(`/api/projects/${projectId}/takeoff/${latest.id}/status`, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      })
      if (!statusRes.ok) return []
      const data = await statusRes.json()
      return (data.items || []).map((i: any) => ({ id: i.id, name: i.name, quantity: i.quantity, unit: i.unit, category: i.category }))
    },
    enabled: needsItems,
  })

  const categories = takeoffItems ? [...new Set(takeoffItems.map(i => i.category))].sort() : []

  const toggleItem = (id: string) => {
    const next = new Set(selectedItems)
    next.has(id) ? next.delete(id) : next.add(id)
    setSelectedItems(next)
  }
  const toggleCategory = (cat: string) => {
    const catItems = takeoffItems?.filter(i => i.category === cat) || []
    const allSelected = catItems.every(i => selectedItems.has(i.id))
    const next = new Set(selectedItems)
    catItems.forEach(i => allSelected ? next.delete(i.id) : next.add(i.id))
    setSelectedItems(next)
  }
  const selectAll = () => {
    if (!takeoffItems) return
    if (selectedItems.size === takeoffItems.length) {
      setSelectedItems(new Set())
    } else {
      setSelectedItems(new Set(takeoffItems.map(i => i.id)))
    }
  }

  // PR3 (C1): a quote request needs SOMETHING to quote — selected takeoff
  // items OR a written description. A general message needs its text.
  const hasContext = context.trim().length > 0
  const canGenerate =
    type === 'quote_request' ? selectedItems.size > 0 || hasContext
      : type === 'general_message' ? hasContext
        : true
  const missingLabel = type === 'general_message'
    ? (isEs ? 'Escribe el mensaje' : 'Write the message')
    : (isEs ? 'Elige partidas o describe qué cotizar' : 'Pick items or describe what to quote')

  const mutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      setGenerating(true)
      const items = needsItems && takeoffItems
        ? takeoffItems.filter(i => selectedItems.has(i.id)).map(i => ({ name: i.name, quantity: i.quantity, unit: i.unit }))
        : undefined
      const res = await apiFetch(`/api/projects/${projectId}/emails/generate`, {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          context,
          language: isEs ? 'es' : 'en',
          items,
          contactId: contactId || undefined,
          manualEmail: (!contactId && manualEmail.trim()) ? manualEmail.trim() : undefined,
        })
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || `Generation failed (${res.status})`)
      }
      return res.json()
    },
    onSuccess: () => { setGenerating(false); setGenerateError(false); onGenerated() },
    // PR #277's defect, re-implemented on today's module (that branch is 367
    // files / -86,886 lines behind main — the fix is one line, the rebase was
    // not): a browser alert() is modal, unstyled, untranslated and it DROPS
    // the message the moment the user dismisses it. Inline, bilingual, and it
    // stays on screen next to the button that failed.
    onError: () => { setGenerating(false); setGenerateError(true) }
  })

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
      <h3 className="text-[13px] font-[800]">{t('Generate Email')}</h3>
      {generateError && (
        <EmailGenerateErrorNotice isEs={isEs} onDismiss={() => setGenerateError(false)} />
      )}
      <p className="text-[10px] font-[600] text-[#9E9890]">{t('AI will draft a professional email for you')}</p>

      {/* Type selector — PR-NEW: only selectable types render
          here. Material orders moved to the Material Orders
          module (see notice below). */}
      <div className="grid grid-cols-3 gap-2">
        {Object.entries(SELECTABLE_EMAIL_TYPES).map(([key, info]) => {
          const Icon = info.icon
          return (
            <button key={key} onClick={() => setType(key)}
              className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-colors ${type === key ? 'border-[#06539B] bg-[#06539B] text-white' : 'border-[#ECEAE4] text-[#9E9890]'}`}>
              <Icon size={18} />
              <span className="text-[9px] font-[700]">{isEs ? info.es : info.en}</span>
            </button>
          )
        })}
      </div>

      {/* PR-NEW: material-order redirect notice. Builders used
          to generate material-order emails from here, but the
          richer Material Orders module owns that flow now
          (supplier-specific drafts with order-by / on-site
          dates). The notice + button keeps the discoverability
          without re-introducing the duplicate type in this
          selector. */}
      <div className="flex items-center gap-2 bg-[#F2F0EB] border border-[#ECEAE4] rounded-lg px-3 py-2">
        <Truck size={14} className="text-[#06539B] flex-shrink-0" />
        <span className="text-[11px] font-[600] text-[#1C1A17] flex-1">
          {isEs
            ? 'Los correos de pedidos de material se crean desde el módulo Pedidos de Material.'
            : 'Material order emails are created from the Material Orders module.'}
        </span>
        <button
          type="button"
          onClick={() => navigate(`/projects/${projectId}/materials`)}
          className="flex items-center gap-1 text-[10px] font-[800] text-white bg-[#06539B] hover:bg-[#14457E] rounded-full px-3 py-1.5 whitespace-nowrap"
          aria-label={isEs ? 'Ir a Pedidos de Material' : 'Go to Material Orders'}
        >
          {isEs ? 'Ir a Pedidos de Material' : 'Go to Material Orders'}
          <ArrowRight size={11} />
        </button>
      </div>

      {/* Recipient selector */}
      <div className="space-y-2">
        <label className="text-[11px] font-[800] text-[#1C1A17] flex items-center gap-1.5">
          <User size={12} className="text-[#9E9890]" />
          {isEs ? 'Destinatario' : 'Recipient'}
          <span className="text-[9px] font-[600] text-[#9E9890]">({isEs ? 'opcional al generar' : 'optional at generation'})</span>
        </label>
        {contactsWithEmail.length > 0 ? (
          <select
            value={contactId}
            onChange={e => { setContactId(e.target.value); if (e.target.value) setManualEmail('') }}
            className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B] bg-white"
          >
            <option value="">{isEs ? '— Seleccionar contacto o escribir email —' : '— Select contact or type email —'}</option>
            {supplierContacts.length > 0 && (
              <optgroup label={isEs ? '🏪 Proveedores (Material Orders)' : '🏪 Suppliers (Material Orders)'}>
                {supplierContacts.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.email}){c.company ? ` — ${c.company}` : ''}
                  </option>
                ))}
              </optgroup>
            )}
            {otherContacts.length > 0 && (
              <optgroup label={isEs ? '📋 Otros contactos' : '📋 Other contacts'}>
                {otherContacts.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.email}){c.company ? ` — ${c.company}` : ''}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        ) : null}
        {!contactId && (
          <input
            type="email"
            value={manualEmail}
            onChange={e => setManualEmail(e.target.value)}
            placeholder={isEs ? 'Email del destinatario (puedes agregarlo después)' : 'Recipient email (you can add it later)'}
            className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
          />
        )}
      </div>

      {/* Item selector for material/quote emails */}
      {needsItems && (
        <div className="border border-[#ECEAE4] rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-[#FAFAF8] border-b border-[#ECEAE4]">
            <span className="text-[11px] font-[800] text-[#1C1A17]">
              {t('Select materials from takeoff')}
              {takeoffItems && <span className="text-[#9E9890] font-[600] ml-1">({selectedItems.size}/{takeoffItems.length})</span>}
            </span>
            {takeoffItems && takeoffItems.length > 0 && (
              <button onClick={selectAll} className="text-[10px] font-[700] text-[#06539B]">
                {selectedItems.size === takeoffItems.length ? t('Deselect all') : t('Select all')}
              </button>
            )}
          </div>
          {!takeoffItems || takeoffItems.length === 0 ? (
            // PR3 (C1): Cuantificación is not sold, so most accounts have no
            // takeoff items. A quote request must still work: the user
            // describes what to quote in the context box below.
            <p className="px-3 py-4 text-[11px] font-[600] text-[#9E9890] text-center" data-testid="email-quote-no-takeoff">
              {isEs
                ? 'No hay partidas de cuantificación. Escribe abajo qué necesitas cotizar.'
                : 'No takeoff items. Describe below what you need quoted.'}
            </p>
          ) : (
            <div className="max-h-[200px] overflow-y-auto">
              {categories.map(cat => {
                const catItems = takeoffItems.filter(i => i.category === cat)
                const catSelected = catItems.filter(i => selectedItems.has(i.id)).length
                const isExpanded = expandedCat === cat
                return (
                  <div key={cat}>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-[#FAFAF8] border-b border-[#ECEAE4] cursor-pointer hover:bg-[#F0EFE9]"
                      onClick={() => setExpandedCat(isExpanded ? null : cat)}>
                      <button onClick={e => { e.stopPropagation(); toggleCategory(cat) }}
                        className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${catSelected === catItems.length ? 'bg-[#06539B] border-[#06539B]' : 'border-[#ECEAE4]'}`}>
                        {catSelected === catItems.length && <Check size={10} className="text-white" />}
                      </button>
                      <span className="text-[10px] font-[700] text-[#9E9890] flex-1">{cat.replace(/_/g, ' ')}</span>
                      <span className="text-[9px] font-[600] text-[#9E9890]">{catSelected}/{catItems.length}</span>
                    </div>
                    {isExpanded && catItems.map(item => (
                      <div key={item.id} onClick={() => toggleItem(item.id)}
                        className="flex items-center gap-2 px-3 py-1 border-b border-[#ECEAE4] cursor-pointer hover:bg-[#FAFAF8]">
                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${selectedItems.has(item.id) ? 'bg-[#06539B] border-[#06539B]' : 'border-[#ECEAE4]'}`}>
                          {selectedItems.has(item.id) && <Check size={8} className="text-white" />}
                        </div>
                        <span className="text-[10px] font-[600] text-[#1C1A17] flex-1 truncate">{item.name}</span>
                        <span className="text-[9px] font-[600] text-[#9E9890] flex-shrink-0">{item.quantity} {item.unit}</span>
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      <textarea
        value={context}
        onChange={e => setContext(e.target.value)}
        placeholder={t('Additional context (optional): supplier name, delivery date, special instructions...')}
        className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B] h-20 resize-none"
      />

      <div className="flex gap-2">
        <button onClick={onClose} className="flex-1 border border-[#ECEAE4] rounded-full py-2 text-[11px] font-[800] text-[#9E9890]">{t('Cancel')}</button>
        <button onClick={() => mutation.mutate()} disabled={generating || !canGenerate}
          className="flex-1 flex items-center justify-center gap-2 bg-[#06539B] text-white rounded-full py-2 text-[11px] font-[800] disabled:opacity-40">
          {generating ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          {generating ? t('Generating...') : !canGenerate ? missingLabel : t('Generate')}
        </button>
      </div>
    </div>
  )
}
